import { ButtonPro, InputPro } from 'components';
import LogoSVG from 'components/atoms/LogoSVG';
import { getMeaningfulApprovals, getApprovalStatusClassName, getApprovalStatusLabel } from 'utils/leaveApprovals';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from 'store';
import { getEmployees } from 'store/MasterData';
import {
  LeaveApproval,
  LeaveRequestItem,
  LeaveTypeResponse
} from 'store/Leaves';
import './AllLeaveRequest.scss';
import {
  getAllLeaveRequests,
  getAssignedLeaveRequests,
  getLeaveTypes
} from 'store/Leaves';
import { UserRoles } from 'models/enums';

interface Props {
  useStatusTitle?: boolean;
}

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All Requests' },
  { value: '0', label: 'Pending' },
  { value: '1', label: 'Approved' },
  { value: '2', label: 'Rejected' },
  { value: '3', label: 'Cancellation Pending' },
  { value: '4', label: 'Cancelled' }
];

const AllLeaveRequests = ({ useStatusTitle = false }: Props) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const hasFetchStarted = useRef(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const userRole = useAppSelector(
    (state) => state.auth.user.authData?.data?.userRole
  );
  const isHrView = userRole === UserRoles.ADMIN;
  const {
    leaveTypeInfo,
    allLeaveRequests,
    allLeaveRequestsLoading,
    allLeaveRequestsError,
    allLeaveRequestsTotalPages,
    allLeaveRequestsTotalCount
  } = useAppSelector((state) => state.leaves);
  const employees = useAppSelector((state) => state.masterData.employees);

  useEffect(() => {
    dispatch(getLeaveTypes());
    dispatch(getEmployees());
  }, [dispatch]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPageNumber(1);
    }, searchTerm.trim() ? 500 : 0);
    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const params = {
      pageNumber,
      pageSize,
      statusFilter: statusFilter || 'all',
      search: debouncedSearch || undefined
    };

    if (isHrView) {
      dispatch(getAllLeaveRequests(params));
    } else {
      dispatch(getAssignedLeaveRequests(params));
    }
  }, [dispatch, pageNumber, pageSize, statusFilter, isHrView, debouncedSearch]);

  useEffect(() => {
    if (allLeaveRequestsLoading) {
      hasFetchStarted.current = true;
      return;
    }
    if (hasFetchStarted.current) {
      setHasLoadedOnce(true);
    }
  }, [allLeaveRequestsLoading]);

  const fetchLeaveRequests = () => {
    const params = {
      pageNumber,
      pageSize,
      statusFilter,
      search: debouncedSearch || undefined
    };

    if (isHrView) {
      dispatch(getAllLeaveRequests(params));
    } else {
      dispatch(getAssignedLeaveRequests(params));
    }
  };

  const handleLeaveCardClick = (leave: LeaveRequestItem) => {
    navigate(
      `/respondLeaveRequest?leaveId=${leave.leaveDetail.detailsId}&updated=${leave.updatedAsTicks}`
    );
  };

  const handleStatusChange = (newStatus: string) => {
    setStatusFilter(newStatus);
    setPageNumber(1);
  };

  function findLeaveType(leaveTypeId: string) {
    return leaveTypeInfo?.find(
      (leaveType: LeaveTypeResponse) => leaveType.leaveTypeID === leaveTypeId
    );
  }

  function findEmpName(id: string) {
    if (!employees || employees.length === 0) return '';
    const employee = employees.find((emp) => emp.employeeId === id);
    return employee ? employee.employeeName : '';
  }

  const currentStatusLabel =
    STATUS_OPTIONS.find((option) => option.value === statusFilter)?.label ??
    'All Requests';

  return (
    <div className="all-leave-requests-container">
      <div className="header-section">
        <h2 className="all-leave-requests-title">
          {useStatusTitle
            ? currentStatusLabel
            : isHrView
            ? 'All Leave Requests'
            : 'My Team Leave Requests'}{' '}
          ({allLeaveRequestsTotalCount})
        </h2>
        <div className="filter-section">
          <InputPro
            value={searchTerm}
            onChange={setSearchTerm}
            name="employeeName"
            label="Employee Name: "
            inputWrapperClass="flex"
          />
          <label htmlFor="status-filter" className="filter-label">
            Filter by Status:
          </label>
          <select
            id="status-filter"
            className="status-dropdown"
            value={statusFilter}
            onChange={(e) => handleStatusChange(e.target.value)}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {allLeaveRequestsLoading && !hasLoadedOnce ? (
        <div className="loading">
          <LogoSVG />
        </div>
      ) : allLeaveRequestsError ? (
        <div style={{ position: 'relative', width: '100%', padding: '20px' }}>
          <div className="error">
            <div>
              <strong>Error:</strong> {allLeaveRequestsError}
            </div>
            <div style={{ marginTop: 12 }}>
              <ButtonPro
                label="Refresh"
                type="primary"
                htmlType="button"
                onClick={() => fetchLeaveRequests()}
              />
              <div style={{ fontSize: 13, color: '#666', marginTop: 8 }}>
                Please try refreshing.
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="all-leave-requests-list">
            {allLeaveRequests.length === 0 ? (
              <div className="all-leave-requests-empty">
                No leave requests match the current filters.
              </div>
            ) : (
              allLeaveRequests.map((leave: LeaveRequestItem) => {
              const meaningfulApprovals = getMeaningfulApprovals(
                leave.leaveApprovals
              );

              return (
              <div
                className="all-leave-card"
                key={leave.leaveDetail.detailsId}
                onClick={() => handleLeaveCardClick(leave)}
              >
                <div className="all-leave-header">
                  <div className="all-leave-info">
                    <h3 className="requester-name">
                      {leave.requesterNickName}
                    </h3>
                    <span className="leave-type-badge">
                      {findLeaveType(leave.leaveDetail.leaveTypeId)
                        ?.leaveType || 'Leave Type'}
                    </span>
                  </div>
                  <div className="leave-status">
                    <span
                      className={`status-badge status-${leave.leaveDetail.leaveStatus}`}
                    >
                      {leave.leaveDetail.leaveStatus === 0
                        ? 'Pending'
                        : leave.leaveDetail.leaveStatus === 1
                        ? 'Approved'
                        : leave.leaveDetail.leaveStatus === 3
                        ? 'Cancellation Pending'
                        : leave.leaveDetail.leaveStatus === 4
                        ? 'Cancelled'
                        : 'Rejected'}
                    </span>
                  </div>
                </div>

                <div className="all-leave-body">
                  <div className="leave-details-grid">
                    <div className="detail-item">
                      <strong>Employee UPN:</strong>
                      <span>{leave.leaveDetail.upn}</span>
                    </div>
                    <div className="detail-item">
                      <strong>Start Date:</strong>
                      <span>{leave.leaveDetail.startDate.slice(0, 10)}</span>
                    </div>
                    <div className="detail-item">
                      <strong>End Date:</strong>
                      <span>{leave.leaveDetail.endDate.slice(0, 10)}</span>
                    </div>
                    <div className="detail-item">
                      <strong>Days:</strong>
                      <span>{leave.leaveDetail.noOfDays}</span>
                    </div>
                    <div className="detail-item">
                      <strong>Session:</strong>
                      <span>
                        {leave.leaveDetail.leaveSession === 0
                          ? 'Morning'
                          : leave.leaveDetail.leaveSession === 1
                          ? 'Evening'
                          : 'Full Day'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <strong>Requested Date:</strong>
                      <span>
                        {new Date(
                          leave.leaveDetail.requestedDate
                        ).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <div className="reason-section">
                    <strong>Reason:</strong>
                    <p className="reason-text">{leave.leaveDetail.reason}</p>
                  </div>

                  {meaningfulApprovals.length > 0 && (
                    <div className="approvals-section">
                      <strong>
                        Approvals ({meaningfulApprovals.length}):
                      </strong>
                      <div className="approvals-list">
                        {meaningfulApprovals.map((approval: LeaveApproval) => (
                          <div key={approval.id} className="approval-item">
                            <span
                              className={getApprovalStatusClassName(approval)}
                            >
                              {getApprovalStatusLabel(approval)}
                            </span>
                            <span className="approval-approver">
                              by {findEmpName(approval.approverId)}
                            </span>
                            <span className="approval-date">
                              {new Date(
                                approval.approvalDate
                              ).toLocaleDateString()}
                            </span>
                            {approval.comments && (
                              <span className="approval-comment">
                                "{approval.comments}"
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {meaningfulApprovals.length === 0 && (
                    <div className="no-approvals">
                      <em>No approval decisions yet</em>
                    </div>
                  )}

                  <div
                    className="all-leave-actions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ButtonPro
                      label={
                        leave.leaveDetail.leaveStatus === 0
                          ? 'Review and Respond'
                          : 'View Details'
                      }
                      type="white"
                      size="small"
                      buttonClassNames="all-leave-btn details"
                      onClick={() => handleLeaveCardClick(leave)}
                    />
                  </div>
                </div>
              </div>
            );
            })
            )}
          </div>

          {allLeaveRequestsTotalPages > 1 && (
            <div className="pagination-controls">
              <ButtonPro
                label="Previous"
                type="white"
                htmlType="button"
                onClick={() => setPageNumber((prev) => Math.max(1, prev - 1))}
                disabled={pageNumber === 1}
              />
              <div className="pagination-info">
                <span>
                  Page {pageNumber} of {allLeaveRequestsTotalPages}
                </span>
                <span className="total-count">
                  ({allLeaveRequestsTotalCount} total items)
                </span>
              </div>
              <ButtonPro
                label="Next"
                type="white"
                htmlType="button"
                onClick={() =>
                  setPageNumber((prev) =>
                    Math.min(allLeaveRequestsTotalPages, prev + 1)
                  )
                }
                disabled={pageNumber === allLeaveRequestsTotalPages}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AllLeaveRequests;
